<template>
	<wrapper-features-templates-operations
	:scrollValue="scrollValue"
	:showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState && !showLoader"
	:hideContentWrapper="showAccountsEmptyState"
	:showStepper="activeStep <= stepperParams.length && showContent"
	:activeStep="activeStep"
	:stepperParams="stepperParams"
	:showFooter="activeStep < 3 && showContent"
	:footerButtonsParams="footerButtonsParams"
	:feedbackOperationData="feedbackOperationData"
	:tokenTitleAppend="t('transfers.international.tokenTitle')"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, submitTransfer)"
	@clearScrollToValue="scrollValue = null"
	@backOrCloseClicked="emit('backOrCloseClicked')">
		<!-- *********************************************
		*                      Content                   *
		********************************************** -->
		<component
		v-if ="showContent"
		:is="getActiveStepComponent"
		:isMobile="getMobileAppState"
		:formData="formData"
        :availableAmountPlafond="availableAmountPlafond"
        :availableAmountTransactionLimit="availableAmountTransactionLimit"
		:confidentialBeneficiary="confidentialBeneficiary"
		:setDefaultRadioOption="setDefaultRadioOption"
		:getAccountsList="getAccountsBalances"
		:clearFormErrors="clearFormErrors"
		:mandatoryList="mandatoryList"
		:showMandatoryErrors="showMandatoryErrors"
		:responseData="responseData"
		@scrollTo="scrollValue = $event"
		@formErrorsCleared="clearFormErrors = false"
		@resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @hideToast="showFeedbackToast = false"
		@updateFormData="updateFormDataHandler"
		@validateNickname="validateNicknameHandler"
		@newTransfer="newTransferHandler"/>
		<!-- Empty-state -->
		<template #empty-state-slot>
			<transition name="common-enter-animation" mode="out-in" appear>
				<empty-state-generic
				v-if="showAccountsEmptyState"
				illustration="emptyState/batchfile-cards"
				:title="t('transfers.kwik.transfer.emptyState.title')"
				:description="t('transfers.kwik.transfer.emptyState.description')"/>
			</transition>
		</template>
	</wrapper-features-templates-operations>
	<!-- Feedback Toasts -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, watch, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { emailValidator, phoneNumberValidator } from '@/assets/scripts/validators'
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestAccountsBalances, checkBeneficiaryAlias, createTransfer, requestAccountsPlafond, requestAccountsTransactionLimit, clearFeatureStates } = mapActions(moduleMapper.TRANSFERS_KWIK)
const { getAccountsBalances, getAccountsPlafond, getAccountsTransactionLimit } = mapGetters(moduleMapper.TRANSFERS_KWIK)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const showAccountsEmptyState = computed(() => {	return !showLoader.value && !applyErrorState.value && !getAccountsBalances.value.length })
const showContent = computed(() => {	return !showLoader.value && !applyErrorState.value && !!getAccountsBalances.value.length })

/* Data */
const scrollValue = ref(null)
const setDefaultRadioOption = ref(false)
const applyErrorState = ref(false)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)
const availableAmountPlafond = ref()
const availableAmountTransactionLimit = ref()

/*************************************************
*                   Steps Logic                  *
*************************************************/
const activeStep = ref(1)

const stepperParams = ref([
	{ description: t('transfers.kwik.transfer.stepper.data') },
	{ description: t('transfers.kwik.transfer.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
	const stepNumber = activeStep.value
	return defineAsyncComponent(() => import('@/components/features/transfers/kwik/transfer/Step' + stepNumber))
})

const goToStep = stepNumber => {
	activeStep.value = stepNumber
	scrollValue.value = 0
}

const newTransferHandler = () => {
	showOverlayLoader.value = true
	goToStep(1)
	clearFormData()
	initialRequests()
}

/*************************************************
*                Form Logic Data                 *
*************************************************/
const confidentialBeneficiary = ref(false)
const beneficiaryError = ref(false)

const formData = ref({
	/* Account Data */
	originAccount: '',
	accountDisplayInfo: '',
	currency: '',
	/* Transfer Data */
	beneficiaryName: '',
	beneficiaryIdType: 'PHONE_NUMBER',
	beneficiaryId: '',
	amount: '',
	description: '',
	descriptionDestination: ''
})

const mandatoryList = ref([
	'beneficiaryId',
	'amount'
])

const updateFormDataHandler = ({ fieldName, inputValue }) => {
	formData.value[fieldName] = inputValue

    if (fieldName === 'originAccount') checkPlafondAndTransactionLimit()
}

const beneficiaryPayloadConstructor = () => {
	let aliasType

	switch (formData.value.beneficiaryIdType) {
		case 'PHONE_NUMBER':
			aliasType = 'PN'
			break
		case 'EMAIL':
			aliasType = 'EM'
			break
		default:
			aliasType = 'NN'
			break
	}

	if (formData.value.beneficiaryIdType === 'IBAN') {
		return {
			iban: formData.value.beneficiaryId
		}
	} else {
		return {
			aliasType: aliasType,
			alias: formData.value.beneficiaryId
		}
	}
}

/*************************************************
*             Submit Transfer Logic              *
*************************************************/
const submitTransfer = async () => {
	showOverlayLoader.value = true

	const payload = {
		amount: formData.value.amount,
		beneficiaryName: formData.value.beneficiaryName,
		currency: formData.value.currency,
		description: formData.value.description,
		descriptionDestination: formData.value.descriptionDestination,
		genericDestination: formData.value.beneficiaryId,
		originAccount: formData.value.originAccount,
		type: 10,
		genericDestinationType: formData.value.beneficiaryIdType,
		executeInRealTime: true
	}

	try {
		await createTransfer(payload)
		showOverlayLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, submitTransfer)
	}
}

/*************************************************
*             Success/Error Handlers             *
*************************************************/
const feedbackOperationData = ref(null)
const responseData = ref()

const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const challengeSuccessHandler = async data => {
	responseData.value = {
		...data.data,
		errorLevel: data.errorLevel,
		code: data.code
	}

	feedbackOperationData.value = {
		status: data.errorLevel,
		title: data.message,
		additionalMessage: data.additionalMessage || '',
		buttonsData: !data.data.hasReceipt ? null : {
			emailAction: 'sendOperationEmailNotification',
			smsAction: 'sendOperationSMSNotification',
			sendPayload: { operationId: data.data.operationId },
			downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
			shareLink: data.data.operationId,
			operationId: data.data.operationId
		}
	}

	goToStep(3)
}

const errorHandler = (data, retryFn) => {
	showLoader.value = false
	showOverlayLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/*************************************************
*               Footer/Buttons Logic             *
*************************************************/
const validateMandatoryFormValues = () => {
	if (formData.value.amount <= 0 || atLeastOneIsEmpty(formData.value, mandatoryList.value) || invalidFields()) {
		showMandatoryErrors.value = true
	} else if (beneficiaryError.value && !showMandatoryErrors.value) {
		validateNicknameHandler()
	} else goToStep(2)
}

/* After validating mandatory fields, check for valid phone number and email input */
const invalidFields = () => {
	if ((formData.value.beneficiaryIdType === 'PHONE_NUMBER' && !phoneNumberValidator(formData.value.beneficiaryId) && !!formData.value.beneficiaryId) ||
		(formData.value.beneficiaryIdType === 'EMAIL' && !emailValidator(formData.value.beneficiaryId) && !!formData.value.beneficiaryId) ||
		(formData.value.beneficiaryIdType === 'IBAN' && formData.value.beneficiaryId.length < 24)) {
		return true
	} else return false
}

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
	return [{
		label: t('transfers.kwik.transfer.btn.clear'),
		buttonPosition: 'left',
		hidden: activeStep.value !== 1,
		callbackFunction: () => { clearFormData() },
		step: 1
	}, {
		label: t('transfers.kwik.transfer.btn.forward'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 1,
		callbackFunction: () => { validateMandatoryFormValues() }
	}, {
		label: t('transfers.kwik.transfer.btn.edit'),
		icon: 'arrow-left-full',
		hidden: activeStep.value !== 2,
		callbackFunction: () => { goToStep(1) }
	}, {
		label: t('transfers.kwik.transfer.btn.validate'),
		icon: 'check-line',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 2,
		callbackFunction: () => { submitTransfer() }
	}]
})

const footerButtonsParams = computed(() => {
	return footerButtonsData.value.filter(ele => !ele.hidden)
})

const clearFormData = () => {
	clearFormErrors.value = true
	clearObjectStrings(formData.value)

	/* Reset form */
	formData.value = {
		...formData.value,
		originAccount: getAccountsBalances.value.find(account => account.default).id,
		accountDisplayInfo: getAccountsBalances.value.find(account => account.default).accountNumber,
		currency: getAccountsBalances.value.find(account => account.default).currency,
		beneficiaryIdType: 'PHONE_NUMBER'
	}

	setDefaultRadioOption.value = false
	feedbackOperationData.value = null
	responseData.value = null
	showOverlayLoader.value = false
}

/*************************************************
*                Initial Requets                 *
*************************************************/
const initialRequests = async () => {
	applyErrorState.value = false
	showLoader.value = true

	try {
		await requestAccountsBalances()
		if (getAccountsBalances.value.length) {
			/* Setting Initial Selected Account */
			formData.value.originAccount = getAccountsBalances.value.find(account => account.default).id
			/* Setting Initial Account Display Info from Default Account */
			formData.value.accountDisplayInfo = getAccountsBalances.value.find(account => account.default).accountNumber
			/* Setting Initial Currency from Default Account */
			formData.value.currency = getAccountsBalances.value.find(account => account.default).currency
		}
        checkPlafondAndTransactionLimit('initialRequest')
		showLoader.value = false
		goToStep(1)
	} catch (error) {
		applyErrorState.value = true
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, initialRequests)
	}
}

const checkPlafondAndTransactionLimit = async (initialRequest) => {
    initialRequest ? showLoader.value = true : showOverlayLoader.value = true

    const payload = {
		id: formData.value.originAccount,
        params: { operationTypeId: 70005 }
	}

    try {
        await requestAccountsPlafond(payload)
        await requestAccountsTransactionLimit(payload)
        availableAmountPlafond.value = getAccountsPlafond.value.availableAmount
        availableAmountTransactionLimit.value = getAccountsTransactionLimit.value.availableAmount
        initialRequest ? showLoader.value = false : showOverlayLoader.value = false
    } catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, checkPlafondAndTransactionLimit)
	}
}

const validateNicknameHandler = async () => {
	showOverlayLoader.value = true
	beneficiaryError.value = false

	const payload = beneficiaryPayloadConstructor()

	try {
		const res = await checkBeneficiaryAlias(payload)
		formData.value.beneficiaryName = res.data.data.beneficiaryName
		showOverlayLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return

		if (error.response.data.code === 'externoalias_association_has_private_name') {
			confidentialBeneficiary.value = true
			return
		}

		beneficiaryError.value = true

		if (error.response.data.code === 'unable_to_find_beneficiary_alias_name' || error.response.data.code === 'XCR-3031, BSN-0168') {
			errorHandler(error.response.data)
		} else errorHandler(error.response.data, validateNicknameHandler)
	}
}

watch(() => activeStep.value, (newVal, oldVal) => {
	scrollValue.value = 0
})

initialRequests()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.button-primary {
	min-width: 200px;

	&.mobile {
		width: 100%;
	}
}
</style>
